// No direct PHP counterpart.
// Shared JS helper for the games Monty controllers:
// - api/fuel/app/classes/controller/api/monty/games/about.php
// - api/fuel/app/classes/controller/api/monty/games/comments.php
// - api/fuel/app/classes/controller/api/monty/games/expansions.php
// - api/fuel/app/classes/controller/api/monty/games/search.php
// - api/fuel/app/classes/controller/api/monty/games/suggest.php
// Role in the port:
// - keep route files thin and PHP-shaped
// - isolate JS-only orchestration where multiple PHP controllers share the same offline plumbing
import F from 'offline/filter/constants'
import DB from 'offline/db'
import Util from 'offline/util'
import Game from 'offline/model/game'
import Comment from 'offline/model/comment'
import Collection from 'offline/model/collection'
import Play from 'offline/model/play'
import Rating from 'offline/model/rating'
import Search from 'offline/model/search'
import User from 'offline/model/user'
import GameVersion from 'offline/model/game/version'
import GameDesigner from 'offline/model/game/designer'
import GamePublisher from 'offline/model/game/publisher'
import GameSubdomain from 'offline/model/game/subdomain'
import GameMechanic from 'offline/model/game/mechanic'
import GameArtist from 'offline/model/game/artist'
import GameCategory from 'offline/model/game/category'
import GameFamily from 'offline/model/game/family'
import Designer from 'offline/model/designer'
import Publisher from 'offline/model/publisher'
import Subdomain from 'offline/model/subdomain'
import Mechanic from 'offline/model/mechanic'
import Artist from 'offline/model/artist'
import Category from 'offline/model/category'
import Family from 'offline/model/family'
import { resolveOwner, resolveUsers, runFilterEndpoint } from 'offline/api/monty'
import GamesFilter from 'offline/filter/games'

function nl2br(text) {
	return String(text || '').replace(/\n/g, '<br />')
}

function decodeEntities(text) {
	if (!text) return ''
	if (typeof document !== 'undefined') {
		var textarea = document.createElement('textarea')
		textarea.innerHTML = text
		return textarea.value
	}

	return String(text)
		.replace(/&amp;/g, '&')
		.replace(/&lt;/g, '<')
		.replace(/&gt;/g, '>')
		.replace(/&quot;/g, '"')
		.replace(/&#39;/g, "'")
}

function relationIds(ModelClass, gameId, field, where) {
	var rows = ModelClass.where(where || [['game_id', '=', gameId]])
	var ids = []
	var keys = Object.keys(rows)
	for (var i = 0; i < keys.length; i++) ids.push(rows[keys[i]].rawData()[field])
	return ids
}

function recordsToArray(ModelClass, ids) {
	if (!ids.length) return []
	var rows = ModelClass.where([[ModelClass._schema_key, 'in', ids]])
	var keys = Object.keys(rows)
	var items = []
	for (var i = 0; i < keys.length; i++) {
		items.push(rows[keys[i]].to_array())
	}
	return items
}

function ownerTypeFromParam(type) {
	if (type === 'users') return F.OWNER_USER
	if (type === 'groups') return F.OWNER_GROUP
	if (type === 'buddies') return F.OWNER_BUDDIES
	return null
}

function currentUserId(params) {
	return params.sessionUserId || Number(DB.meta('sync_user_id') || 0) || null
}

function resolveCurrentUser(params) {
	var userId = currentUserId(params)
	return userId ? User.with_id(userId) : null
}

function resolveSearchUser(params) {
	return User.with_id(2574198) || resolveCurrentUser(params) || User.record({})
}

function uniqueIds(ids) {
	var seen = {}
	var result = []

	for (var i = 0; i < ids.length; i++) {
		var id = Number(ids[i])
		if (!id || seen[id]) continue
		seen[id] = true
		result.push(id)
	}

	return result
}

function intersectIds(left, right) {
	var include = {}
	var result = []
	var i

	for (i = 0; i < right.length; i++) {
		include[Number(right[i])] = true
	}

	for (i = 0; i < left.length; i++) {
		var id = Number(left[i])
		if (include[id]) result.push(id)
	}

	return uniqueIds(result)
}

function groupBy(items, field) {
	var grouped = {}

	for (var i = 0; i < items.length; i++) {
		var key = items[i].rawData()[field]
		if (!grouped[key]) grouped[key] = []
		grouped[key].push(items[i])
	}

	return grouped
}

function searchText(params) {
	return Util.sortname((params.text || '').toLowerCase())
}

function matchingGameIds(text) {
	if (!text) return []

	var direct = Object.keys(Game.filter({
		where: [['game_sortname', 'like', '%' + text + '%']],
		indexed: 'game_id'
	})).map(Number)
	var versions = Object.keys(GameVersion.filter({
		where: [['game_version_sort', 'like', '%' + text + '%']],
		indexed: 'game_id'
	})).map(Number)

	return uniqueIds(direct.concat(versions))
}

function loadGamesByIds(ids) {
	var games = {}
	var rows
	var i

	if (!ids.length) return []

	rows = Game.filter({
		where: [['game_id', 'in', ids]]
	})

	for (i = 0; i < rows.length; i++) {
		games[rows[i].get_id()] = rows[i]
	}

	return ids.map(function(id) {
		return games[id] || null
	}).filter(Boolean)
}

function loadGamesForSuggestions(ids, limit) {
	var rows

	if (!ids.length) return []

	rows = Game.filter({
		where: [['game_id', 'in', ids]],
		order: {
			'LENGTH(game_sortname)': 'asc',
			'game_name': 'asc'
		},
		limit: limit
	})

	return rows
}

function collectionRowsForSuggestions(search, source, params, user) {
	var where = []

	if (source === 'collection') {
		if (!user) return []
		where.push(['user_id', '=', user.get_id()])
	} else {
		if (!search) return []
		where.push(['collection_id', 'in', search.get_result_index()])
	}

	where.push(['collection_id', 'not in', params.excludeCollection || [0]])

	if (params.excludeNested) {
		where.push(['collection_nested', '=', 0])
	}

	return Collection.filter({ where: where })
}

function gameSuggestions(games) {
	var suggestions = {}

	for (var i = 0; i < games.length; i++) {
		suggestions[games[i].get_id()] = games[i].to_array()
	}

	return suggestions
}

export function resolveGame(params) {
	if (!params.id) return null
	return Game.with_id(params.id)
}

export function buildAboutResponse(params) {
	var game = resolveGame(params)
	if (!game) return Promise.reject({ error: 'Game not found', offline: true })

	var version = params.version ? GameVersion.with_id(params.version) : null
	var designers = relationIds(GameDesigner, game.get_id(), 'designer_id')
	var publishers = relationIds(GamePublisher, game.get_id(), 'publisher_id', [
		['game_id', '=', game.get_id()],
		['game_version_id', '=', version ? version.get_id() : 0],
		['game_publisher_primary', '=', 1]
	])
	var subdomains = relationIds(GameSubdomain, game.get_id(), 'subdomain_id')
	var mechanics = relationIds(GameMechanic, game.get_id(), 'mechanic_id')
	var artists = relationIds(GameArtist, game.get_id(), 'artist_id')
	var categories = relationIds(GameCategory, game.get_id(), 'category_id')
	var families = relationIds(GameFamily, game.get_id(), 'family_id')

	return Promise.resolve({
		session: { offline: true },
		game: {
			id: game.get_id(),
			name: game.get_name(),
			rank: game.get_rank() || 0,
			description: nl2br(decodeEntities(game.get_description() || '')),
			published: game.get_published() || 0,
			designers: designers,
			subdomains: subdomains,
			publishers: publishers,
			mechanics: mechanics,
			artists: artists,
			categories: categories,
			families: families,
			version: version ? version.to_array() : { name: '', year: '' }
		},
		version: version ? version.to_array() : { name: '', year: '' },
		data: {
			designers: recordsToArray(Designer, designers),
			languages: [],
			publishers: recordsToArray(Publisher, publishers),
			mechanics: recordsToArray(Mechanic, mechanics),
			artists: recordsToArray(Artist, artists),
			subdomains: recordsToArray(Subdomain, subdomains),
			categories: recordsToArray(Category, categories),
			families: recordsToArray(Family, families)
		}
	})
}

export function buildCommentsResponse(params) {
	var game = resolveGame(params)
	if (!game) return Promise.reject({ error: 'Game not found', offline: true })

	var ownerType = ownerTypeFromParam(params.type)
	var owner = ownerType ? resolveOwner({ id: params.owner, slug: params.name, name: params.name }, ownerType) : null
	if (!owner) return Promise.reject({ error: 'Invalid owner', offline: true })

	var userIds
	if (ownerType === F.OWNER_USER) {
		userIds = [owner.get_id()]
	} else {
		userIds = resolveUsers(owner, ownerType, {})
	}

	var comments = Comment.collection({
		where: [
			['game_id', '=', game.get_id()],
			['user_id', 'in', userIds.length ? userIds : [0]]
		],
		order: {
			comment_date: 'desc'
		}
	}).map(function(comment) {
		return comment.to_array()
	})

	return Promise.resolve({
		session: { offline: true },
		comments: comments
	})
}

export function buildExpansionsResponse(params) {
	var game = resolveGame(params)
	if (!game) return Promise.reject({ error: 'Game not found', offline: true })

	var ownerType = ownerTypeFromParam(params.type)
	var owner = ownerType ? resolveOwner({ id: params.owner }, ownerType) : null
	if (!owner) return Promise.reject({ error: 'Invalid owner', offline: true })

	return runFilterEndpoint(GamesFilter, {
		filter: {
			base: [game.get_id()],
			games: {
				exclude: params.exclude || []
			}
		},
		page: 1,
		sessionUserId: params.sessionUserId || null,
		id: owner.get_id()
	}, ownerType, {
		owner: owner
	}).then(function(response) {
		return {
			session: { offline: true },
			expansions: response.games || []
		}
	})
}

export function buildSearchResponse(params) {
	var owner = resolveSearchUser(params)
	var filter = params.filter || {}
	var results
	var games
	var payload
	var i

	if (params.text && !filter.text) {
		filter = Object.assign({}, filter, { text: params.text })
	}

	if (!owner) return Promise.resolve({ session: { offline: true }, results: [] })

	results = GamesFilter.forge({
		owner: owner,
		filter: filter,
		asIndex: true,
		page: 1,
		perpage: params.limit || 50,
		sessionID: currentUserId(params),
		userID: owner.get_id(),
		settings: { session: {}, owner: {} },
		can: {}
	}).run()

	results = results.slice(0, params.limit || 50)

	games = loadGamesByIds(results)
	payload = []

	for (i = 0; i < games.length; i++) {
		payload.push({
			id: games[i].get_id(),
			name: games[i].get_name(),
			year: games[i].get_published() || 0
		})
	}

	return Promise.resolve({
		session: { offline: true },
		results: payload
	})
}

export function buildSuggestResponse(params) {
	var text = searchText(params)
	var source = params.source || false
	var results = params.results || false
	var exclude = params.exclude || [0]
	var limit = params.limit || 25
	var currentUser = resolveCurrentUser(params)
	var universeUser = params.name ? resolveOwner({ slug: params.name }, F.OWNER_USER) : currentUser
	var search = false
	var gameIds
	var games
	var suggestions = {}
	var collection
	var collectionByGame
	var playedIds
	var universeSources
	var universeIds
	var collectionRows
	var playRows
	var ratingRows
	var i

	if (!text) {
		return Promise.resolve({ session: { offline: true }, suggestions: {} })
	}

	gameIds = matchingGameIds(text)
	gameIds = gameIds.filter(function(id) {
		return exclude.indexOf(id) === -1
	})

	if (source && source !== 'collection' && source !== 'plays' && source !== 'universe') {
		search = Search.with('search_key', source)
		if (search) {
			gameIds = intersectIds(gameIds, search.get_game_index())
		}
	}

	if (results === 'collection' || source === 'collection') {
		collection = collectionRowsForSuggestions(search, source, params, currentUser)
		collectionByGame = groupBy(collection, 'game_id')
		gameIds = intersectIds(gameIds, Object.keys(collectionByGame).map(Number))
		games = loadGamesForSuggestions(gameIds, limit)

		if (results === 'collection') {
			for (i = 0; i < games.length && Object.keys(suggestions).length < limit; i++) {
				var gameCollection = collectionByGame[games[i].get_id()] || []
				for (var j = 0; j < gameCollection.length && Object.keys(suggestions).length < limit; j++) {
					suggestions[gameCollection[j].get_id()] = {
						id: gameCollection[j].get_id(),
						name: games[i].get_name()
					}
				}
			}
		} else {
			suggestions = gameSuggestions(games)
		}
	} else if (source === 'plays') {
		if (!currentUser) {
			return Promise.resolve({ session: { offline: true }, suggestions: {} })
		}

		playedIds = Object.keys(Play.filter({
			where: [
				['user_id', '=', currentUser.get_id()],
				['game_id', 'in', gameIds.length ? gameIds : [0]]
			],
			indexed: 'game_id'
		})).map(Number)

		games = loadGamesForSuggestions(playedIds, limit)
		suggestions = gameSuggestions(games)
	} else if (source === 'universe') {
		if (!universeUser) {
			return Promise.resolve({ session: { offline: true }, suggestions: {} })
		}

		universeSources = {}
		collectionRows = Collection.filter({ where: [['user_id', '=', universeUser.get_id()]] })
		playRows = Play.filter({ where: [['user_id', '=', universeUser.get_id()]] })
		ratingRows = Rating.filter({ where: [['user_id', '=', universeUser.get_id()]] })

		for (i = 0; i < collectionRows.length; i++) {
			var collectionRow = collectionRows[i]
			var collectionGameId = collectionRow.get_game_id()
			if (!universeSources[collectionGameId]) universeSources[collectionGameId] = []
			if (collectionRow.get_own() && universeSources[collectionGameId].indexOf('owned') === -1) universeSources[collectionGameId].push('owned')
			if (collectionRow.get_wishlist() > 0 && universeSources[collectionGameId].indexOf('wishlisted') === -1) universeSources[collectionGameId].push('wishlisted')
		}

		for (i = 0; i < playRows.length; i++) {
			var playGameId = playRows[i].get_game_id()
			if (!universeSources[playGameId]) universeSources[playGameId] = []
			if (universeSources[playGameId].indexOf('played') === -1) universeSources[playGameId].push('played')
		}

		for (i = 0; i < ratingRows.length; i++) {
			var ratingGameId = ratingRows[i].get_game_id()
			if (!universeSources[ratingGameId]) universeSources[ratingGameId] = []
			if (universeSources[ratingGameId].indexOf('rated') === -1) universeSources[ratingGameId].push('rated')
		}

		universeIds = Object.keys(universeSources).map(Number)
		gameIds = intersectIds(gameIds, universeIds)
		games = loadGamesForSuggestions(gameIds, limit)

		for (i = 0; i < games.length; i++) {
			suggestions[games[i].get_id()] = {
				id: games[i].get_id(),
				name: games[i].get_name(),
				year: games[i].get_published() || 0,
				thumbnail: games[i].get_avatar(),
				types: universeSources[games[i].get_id()] || []
			}
		}
	} else {
		games = loadGamesForSuggestions(gameIds, limit)
		suggestions = gameSuggestions(games)
	}

	return Promise.resolve({
		session: { offline: true },
		suggestions: suggestions
	})
}
